import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CampaignService,
  CampaignStatistics,
  loadCampaignStatistics,
} from '../../services/campaign.service';

export interface CampaignMetric {
  title: string;
  primaryValue: string;
  secondaryValue: string;
  primaryLabel: string;
  secondaryLabel: string;
  primaryChange?: string;
  secondaryChange?: string;
  gradient: string;
  tooltip: string;
}

/**
 * Displays aggregated campaign performance metrics as a set of summary metric cards and handles loading/updating campaign statistics from a service.
 * @component
 * @selector app-campaign-metrics
 * @example
 *   <app-campaign-metrics [metrics]="sampleMetrics"></app-campaign-metrics>
 * @input {CampaignMetric[]} metrics - Initial array of metric card definitions. The component will update this array after loading statistics from the CampaignService; provide this to pre-seed or override displayed metric cards.
 * @output {none} none - This component does not emit any output events; it retrieves data via the injected CampaignService and updates its internal state.
 *
 * Purpose:
 *   - Summarizes campaign statistics (spend, gain, messages sent, conversion, engagement) into presentable metric cards with labels, formatted values, gradients and tooltips.
 *   - Loads campaign statistics on initialization and keeps a simplified, display-ready representation in the metrics array.
 *
 * Key public properties:
 *   - metrics: CampaignMetric[] - Input array of metric definitions (default: []) which will be replaced/updated when statistics are loaded.
 *   - isTouch: boolean - Detects touch-capable devices (used for UI/tooltip behavior).
 *   - statistics: CampaignStatistics | null - Holds the raw statistics returned from the service.
 *   - loading: boolean - Indicates whether statistics are currently being loaded.
 *
 * Important lifecycle hooks:
 *   - ngOnInit(): Called on component initialization. Triggers loadStatistics() to fetch campaign statistics.
 *
 * Key methods:
 *   - loadStatistics(): Initiates fetching of campaign statistics via CampaignService (delegates to a shared loadCampaignStatistics helper), sets loading state, and stores returned statistics. On success triggers updateMetrics().
 *   - updateMetrics(): Transforms this.statistics into the metrics array consumed by the template. Safely guards against missing statistics and formats primary/secondary values, labels, gradients and tooltips for each displayed card.
 *   - formatNumber(value: string): string - Utility to format large integers into human-friendly strings with K/M suffixes (e.g., 1500 -> "1.5K", 2500000 -> "2.5M"); falls back to numeric string for smaller values.
 *   - isZero(value: string | undefined): boolean - Determines whether a provided value (possibly containing currency or percent symbols) represents zero or is not a number; used to choose placeholder display.
 *   - displayValue(value: string | undefined): string - Returns a display-ready string, mapping zero/undefined values to '-' and otherwise returning the provided value.
 *
 * Dependencies:
 *   - Injects CampaignService in the constructor for fetching statistics. Uses a helper loadCampaignStatistics to orchestrate API calls and loading state updates.
 *
 * Notes:
 *   - No output events are emitted; parent components observe changes by binding to inputs or by relying on shared services if needed.
 *   - The component prepares tooltips and gradient styles per metric card for enhanced UI presentation.
 */
@Component({
  selector: 'app-campaign-metrics',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './campaign-metrics.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './campaign-metrics.component.scss',
})
export class CampaignMetricsComponent implements OnInit {
  isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  @Input() metrics: CampaignMetric[] = [
    {
      title: 'Spend vs Gain',
      primaryValue: '-',
      secondaryValue: '-',
      primaryLabel: 'this month',
      secondaryLabel: 'this month',
      primaryChange: '—',
      secondaryChange: '—',
      gradient: 'linear-gradient(180deg, rgba(133, 223, 156, 1) 0%, rgba(72, 165, 95, 1) 100%)',
      tooltip: 'Total amount spent and revenue earned across campaigns.',
    },
    {
      title: 'Messages Sent',
      primaryValue: '-',
      secondaryValue: '-',
      primaryLabel: 'Emails',
      secondaryLabel: 'Text Messages',
      gradient: 'linear-gradient(180deg, rgba(163, 199, 255, 1) 0%, rgba(81, 134, 217, 1) 100%)',
      tooltip: 'Total number of campaign messages sent across channels (email + text). ',
    },
    {
      title: 'Conversion Metrics',
      primaryValue: '-',
      secondaryValue: '-',
      primaryLabel: 'Conversion Rate',
      secondaryLabel: 'ROI',
      gradient: 'linear-gradient(180deg, rgba(175, 165, 246, 1) 0%, rgba(136, 120, 242, 1) 100%)',
      tooltip:
        'Success rate of turning prospects into car wash customers, shown as conversion rate and Return on Investment (ROI).',
    },
    {
      title: 'Engagement Metrics',
      primaryValue: '-',
      secondaryValue: '-',
      primaryLabel: 'Avg. Open Rate',
      secondaryLabel: 'Avg. Click Rate',
      gradient: 'linear-gradient(180deg, rgba(142, 217, 246, 1) 0%, rgba(73, 163, 215, 1) 100%)',
      tooltip:
        'Measures audience interaction with the campaigns, shown as open rate (who opened) and click rate (who clicked).',
    },
  ];
  statistics: CampaignStatistics | null = null;
  loading = true;

  constructor(private campaignService: CampaignService) {}

  ngOnInit() {
    this.loadStatistics();
  }

  loadStatistics() {
    loadCampaignStatistics(
      this.campaignService,
      stats => {
        this.statistics = stats;
        this.updateMetrics();
      },
      value => {
        this.loading = value;
      }
    );
  }

  /**
   * Populate the component's metrics array from the current this.statistics object if available.
   * @example
   * pdateMetrics()
   * // undefined
   * @returns {void} Updates this.metrics on the component instance; does not return a value.
   */
  updateMetrics() {
    if (!this.statistics) return;

    const stats = this.statistics.statistics;
    const spendMom = this.formatMomPercentChange(stats.this_month_spend, stats.prev_month_spend);
    const gainMom = this.formatMomPercentChange(stats.this_month_gain, stats.prev_month_gain);

    this.metrics = [
      {
        title: 'Spend vs Gain',
        primaryValue: stats.spend !== null && stats.spend !== undefined ? `$${stats.spend}` : '-',
        secondaryValue: stats.gain !== null && stats.gain !== undefined ? `$${stats.gain}` : '-',
        primaryLabel: 'this month',
        secondaryLabel: 'this month',
        primaryChange: spendMom,
        secondaryChange: gainMom,
        gradient: 'linear-gradient(180deg, rgba(133, 223, 156, 1) 0%, rgba(72, 165, 95, 1) 100%)',
        tooltip: 'Total amount spent and revenue earned across campaigns.',
      },
      {
        title: 'Messages Sent',
        primaryValue: this.formatNumber(stats.total_emails_sent),
        secondaryValue: this.formatNumber(stats.total_sms_sent),
        primaryLabel: 'Emails',
        secondaryLabel: 'Text Messages',
        gradient: 'linear-gradient(180deg, rgba(163, 199, 255, 1) 0%, rgba(81, 134, 217, 1) 100%)',
        tooltip: 'Total number of campaign messages sent across channels (email + text). ',
      },
      {
        title: 'Conversion Metrics',
        primaryValue: `${stats.conversion_rate}%`,
        secondaryValue: `${stats.roi}%`,
        primaryLabel: 'Conversion Rate',
        secondaryLabel: 'ROI',
        gradient: 'linear-gradient(180deg, rgba(175, 165, 246, 1) 0%, rgba(136, 120, 242, 1) 100%)',
        tooltip:
          'Success rate of turning prospects into car wash customers, shown as conversion rate and Return on Investment (ROI).',
      },
      {
        title: 'Engagement Metrics',
        primaryValue: `${stats.avg_open_rate}%`,
        secondaryValue: `${stats.avg_click_rate}%`,
        primaryLabel: 'Avg. Open Rate',
        secondaryLabel: 'Avg. Click Rate',
        gradient: 'linear-gradient(180deg, rgba(142, 217, 246, 1) 0%, rgba(73, 163, 215, 1) 100%)',
        tooltip:
          'Measures audience interaction with the campaigns, shown as open rate (who opened) and click rate (who clicked).',
      },
    ];
  }

  /**
   * Month-over-month % change: ((current − previous) / previous) × 100.
   * If both months are zero or previous is missing, returns "—". If previous is 0 but current &gt; 0, shows as new spend (no meaningful ratio).
   */
  formatMomPercentChange(
    current: string | number | undefined | null,
    previous: string | number | undefined | null
  ): string {
    const cur = parseFloat(String(current ?? '').replace(/,/g, ''));
    const prev = parseFloat(String(previous ?? '').replace(/,/g, ''));
    if (Number.isNaN(cur) || Number.isNaN(prev)) {
      return '—';
    }
    if (prev === 0 && cur === 0) {
      return '—';
    }
    if (prev === 0) {
      return cur > 0 ? 'New' : '—';
    }
    const pct = ((cur - prev) / prev) * 100;
    const sign = pct > 0 ? '+' : '';
    const abs = Math.abs(pct);
    const formatted = abs >= 1000 ? pct.toFixed(0) : pct.toFixed(1);
    return `${sign}${formatted}%`;
  }

  formatNumber(value: string): string {
    const num = parseInt(value, 10);
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return String(num);
  }

  isZero(value: string | undefined): boolean {
    if (!value) return true;
    // Remove common prefixes/suffixes and check if the numeric part is zero
    const numericValue = value.replace(/[$%]/g, '').trim();
    const num = parseFloat(numericValue);
    return isNaN(num) || num === 0;
  }

  displayValue(value: string | undefined): string {
    return this.isZero(value) ? '-' : value || '-';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
